import { useEffect, useMemo, useState } from "react";
import cn from "classnames";

import { SectionModule, TextBreakpointModule, ImageBreakpointModule } from "@/src/component/partial/__module__";
import { ProviderHooks, StyleHooks } from "@/src/hook";
import { FlowSpinner } from "@/src/component/partial/FlowSpinner";
import { StrapiComponentType, ComponentType } from "@/src/type";

import { MessageCarousel } from "./component";

import styles from "./InlineFrame.module.scss";

function frameStyle(url?: string) {
  if ((url ?? "").includes("matterport.com")) {
    return styles.matterport;
  }
  return;
}

interface InlineFrameProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.InlineFrame> {}

function InlineFrame({ section, slug, snapToGrid, classes, searchParams, quality, layout, lazy }: InlineFrameProps) {
  const inlineFrame: StrapiComponentType.Section.InlineFrame = section.type?.[0];
  const [showLoader, setShowLoader] = useState(typeof inlineFrame.showLoader === "boolean" ? inlineFrame.showLoader : false);
  const [removeLoader, setRemoveLoader] = useState(typeof inlineFrame.showLoader === "boolean" ? !inlineFrame.showLoader : true);
  const isMobile = StyleHooks.useBreakPointDown("sm");
  const property = ProviderHooks.useProperty();
  const hasBackground = (inlineFrame.loaderBackground ?? []).length > 0;

  /**
   * this is specifically to get a virtual tour url from the property floorplan
   */
  const virtualTourUrl = useMemo(() => {
    if (!inlineFrame.useProviderForUrl) {
      return inlineFrame.url;
    }

    if (inlineFrame.providerType === "property") {
      for (const floorplan of property?.floorplans ?? []) {
        const virtualTour = floorplan?.virtualTours?.find((virtualTour) => (virtualTour?.slug ?? "").includes(slug));
        if (virtualTour) {
          return virtualTour.virtualTourUrl;
        }
      }
    }
    return inlineFrame.url;
  }, [inlineFrame.useProviderForUrl, inlineFrame.providerType, inlineFrame.url, property?.floorplans, slug]);

  /**
   * this gets properties to add to the iframe from the CMS
   */
  const frameProps = useMemo(() => {
    const thisProps: Record<string, string | boolean | number> = {};
    for (const frameProp of inlineFrame.frameProperties) {
      if (frameProp.value === "true" || frameProp.value === "false") {
        thisProps[frameProp.key] = frameProp.value === "true";
      } else if (!isNaN(Number(frameProp.value))) {
        thisProps[frameProp.key] = Number(frameProp.value);
      } else {
        thisProps[frameProp.key] = frameProp.value;
      }
    }
    return thisProps;
  }, [inlineFrame.frameProperties]);

  /**
   * this helps with accesssibility when using a background image
   */
  function getLoaderBackground() {
    switch (inlineFrame.loaderBackgroundGradient) {
      case "overlayLinearGradient1":
        return styles.overlayLinearGradient1;
      case "overlayLinearGradient2":
        return styles.overlayLinearGradient2;
      case "overlayLinearGradient3":
        return styles.overlayLinearGradient3;
      case "overlayLinearGradient4":
        return styles.overlayLinearGradient4;
      default:
        return styles.overlayLinearGradient0;
    }
  }

  useEffect(() => {
    setTimeout(() => {
      setShowLoader(false);
      setTimeout(() => {
        setRemoveLoader(true);
      }, 2000);
    }, inlineFrame.loaderDelayMs ?? 12000);
  }, [inlineFrame.loaderDelayMs]);

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: classes?.section,
      }}
      layout={layout}
      stylePrefix="InlineFrame"
      styleContainerId={inlineFrame.id}
      snapToGrid={snapToGrid}
      searchParams={searchParams}
      quality={quality}
      lazy={lazy}
    >
      <iframe
        className={cn(styles.iframe, frameStyle(virtualTourUrl ?? inlineFrame.url))}
        src={virtualTourUrl ?? inlineFrame.url}
        id={inlineFrame.frameId}
        title={inlineFrame.frameId}
        width="100%"
        height="100%"
        {...frameProps}
      />
      {!removeLoader && (
        <div className={cn(styles.loader, showLoader && styles.show, hasBackground && styles.hasBackground)}>
          {showLoader && (
            <div className={styles.loaderContent}>
              <div className={styles.loaderSpinner}>
                <FlowSpinner
                  height={isMobile ? 120 : 230}
                  width={isMobile ? 120 : 230}
                  color="secondary"
                />
              </div>
              {inlineFrame.loaderTitle && (
                <TextBreakpointModule
                  id={`iframe-title-${inlineFrame.id}`}
                  text={inlineFrame.loaderTitle}
                  className={styles.loaderTitle}
                />
              )}
              {inlineFrame.loaderMessages && (
                <MessageCarousel
                  id={`iframe-messages-${inlineFrame.id}`}
                  messages={inlineFrame.loaderMessages}
                  autoPlayDelay={inlineFrame.autoScrollIntervalMs ?? 4000}
                  className={styles.loaderMessages}
                />
              )}
            </div>
          )}
          {!showLoader && (
            <div className={styles.loaderContent}>
              {inlineFrame.loaderReleaseMessage && (
                <TextBreakpointModule
                  id={`iframe-release-title-${inlineFrame.id}`}
                  text={inlineFrame.loaderReleaseMessage}
                  className={styles.loaderTitle}
                />
              )}
            </div>
          )}
        </div>
      )}
      <div className={cn(styles.loaderBackgroundGradient, getLoaderBackground())} />
      {hasBackground && inlineFrame.loaderBackground && (
        <ImageBreakpointModule
          id={`iframe-bg-image-${inlineFrame.id}`}
          images={inlineFrame.loaderBackground}
          classes={{
            container: styles.loaderBackgroundImage,
          }}
          quality={quality}
          lazy={lazy}
        />
      )}
    </SectionModule>
  );
}

export default InlineFrame;
